import {
  CartesianGrid,
  ReferenceArea,
  ReferenceLine,
  ResponsiveContainer,
  Scatter,
  ScatterChart,
  Tooltip,
  XAxis,
  YAxis,
  ZAxis,
} from "recharts";
import { ChartTooltipShell } from "../panel";
import { TIER_COLOR, tierBands, tierForScore } from "../macro-tier-colors";
import type { MacroTier, MacroVariablePoint } from "../../../pages/PortfolioPerformance/lib/api/macro-exposure";
import type { SensitivityTier } from "../../../pages/PortfolioPerformance/lib/api/shared";

const AXIS_COLOR = "var(--neutral-inactive)";

// The axis runs a little past 100 so a bubble at 100 is drawn whole.
const Y_AXIS_MAX = 115;

function TierWord({ tier }: { tier: SensitivityTier }) {
  return (
    <span className="font-medium" style={{ color: TIER_COLOR[tier] }}>
      {tier}
    </span>
  );
}

function VariableTooltip({
  active,
  payload,
  tiers,
}: {
  active?: boolean;
  payload?: Array<{ payload: MacroVariablePoint & { x: number } }>;
  tiers?: MacroTier[];
}) {
  if (!active || !payload?.length) return null;
  const p = payload[0]!.payload;
  return (
    <ChartTooltipShell>
      <p className="font-semibold text-foreground">{p.name}</p>
      {/* Only the band, never the score behind it: the variable's sensitivity now and last quarter. */}
      <p className="text-muted-foreground">
        Sensitivity now <TierWord tier={tierForScore(p.level ?? 0, tiers)} />
        {p.previousLevel !== null ? (
          <>
            {" "}
            (was <TierWord tier={tierForScore(p.previousLevel, tiers)} />)
          </>
        ) : null}
      </p>
      <p className="text-muted-foreground">Averaged over {p.segments} industries</p>
      <p className="mt-1 text-foreground">Click for the last 4 quarters.</p>
    </ChartTooltipShell>
  );
}

/**
 * One bubble per macro variable, straight from macro_sensitivity_factor_delta (averaged over the industries scored):
 * height = the variable's sensitivity score on the same 0-100 scale as the industries (its value now), read off the
 * y axis as Low / Medium / High; colour = its band. The score itself is never printed, only the band. The x axis is
 * alphabetical by variable name. A variable with no value this quarter has nothing to plot, so it is left off and the page
 * says so.
 */
export function VariableBubbleChart({
  points,
  onSelect,
  height = 300,
  tiers,
}: {
  points: MacroVariablePoint[];
  onSelect: (point: MacroVariablePoint) => void;
  height?: number;
  /** The sensitivity bands (Low / Medium / High): they label the y axis and colour the bubbles. */
  tiers?: MacroTier[];
}) {
  const bands = tierBands(tiers, Y_AXIS_MAX);
  const plotted = points
    .filter((p) => p.level !== null)
    .sort((a, b) => a.name.localeCompare(b.name));
  const withX = plotted.map((p, i) => ({
    ...p,
    x: i,
    sensitivity: p.level as number,
    tier: tierForScore(p.level as number, tiers),
  }));
  const byTier = bands.map((b) => ({ tier: b.tier, data: withX.filter((p) => p.tier === b.tier) }));

  return (
    <div style={{ height }} className="w-full">
      <ResponsiveContainer width="100%" height="100%">
        <ScatterChart margin={{ top: 12, right: 16, bottom: 24, left: 8 }}>
          <CartesianGrid stroke="var(--border)" strokeDasharray="2 4" vertical={false} />
          <XAxis
            type="number"
            dataKey="x"
            domain={[-1, plotted.length]}
            ticks={withX.map((p) => p.x)}
            tickFormatter={(v: number) => withX[v]?.name.slice(0, 16) ?? ""}
            tick={{ fill: AXIS_COLOR, fontSize: 9 }}
            stroke={AXIS_COLOR}
            angle={-30}
            textAnchor="end"
            height={50}
          />
          <YAxis
            type="number"
            dataKey="sensitivity"
            // Fixed at 0..115 like the industry charts; a variable scored below 0 (a different scale in a test
            // environment) pulls the floor down so its bubble is still on the chart.
            domain={[(dataMin: number) => Math.min(0, Math.floor(dataMin)), Y_AXIS_MAX]}
            ticks={bands.map((b) => b.mid)}
            tickFormatter={(v: number) => bands.find((b) => b.mid === v)?.tier ?? ""}
            tick={{ fill: AXIS_COLOR, fontSize: 11 }}
            tickLine={false}
            stroke={AXIS_COLOR}
            width={64}
            label={{
              value: "Sensitivity",
              angle: -90,
              position: "insideLeft",
              fill: AXIS_COLOR,
              fontSize: 11,
            }}
          />
          {bands.map((b) => (
            <ReferenceArea key={`area-${b.tier}`} y1={b.from} y2={b.to} fill={TIER_COLOR[b.tier]} fillOpacity={0.05} />
          ))}
          {bands.slice(1).map((b) => (
            <ReferenceLine key={b.tier} y={b.from} stroke="var(--border)" strokeDasharray="2 4" />
          ))}
          <ZAxis type="number" dataKey="sensitivity" range={[120, 900]} />
          <Tooltip content={<VariableTooltip tiers={tiers} />} />
          {byTier.map(({ tier, data }) => (
            <Scatter
              key={tier}
              name={tier}
              data={data}
              fill={TIER_COLOR[tier]}
              fillOpacity={0.6}
              stroke={TIER_COLOR[tier]}
              isAnimationActive={false}
              cursor="pointer"
              onClick={(entry: unknown) => {
                const point = (entry as { payload?: MacroVariablePoint })?.payload;
                if (point) onSelect(point);
              }}
            />
          ))}
        </ScatterChart>
      </ResponsiveContainer>
    </div>
  );
}
